import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { DemoShowcase } from "@/features/landing/DemoShowcase";
import { WhatIsSection } from "@/features/landing/WhatIsSection";
import { useAuth } from "@/hooks/useAuth";
import { ROUTES } from "@/constants/routes";

const VERBS = ["Shared", "Viewed", "Saved", "Remembered"];
const CTA_LABEL = "Build Your Complete Digital Presence";

function PrimaryCta({ session, testId }) {
  return (
    <Link to={session ? ROUTES.dashboard : ROUTES.signup} data-testid={testId} className="mw-btn mw-btn-primary group">
      {session ? "Open dashboard" : CTA_LABEL}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
    </Link>
  );
}

export default function LandingPage() {
  const { session } = useAuth();
  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-background">
      <header className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-6 sm:px-10">
        <Wordmark to={ROUTES.home} />
        <Link to={session ? ROUTES.dashboard : ROUTES.login} data-testid="header-signin-link" className="mw-link text-sm">
          {session ? "Dashboard" : "Sign in"}
        </Link>
      </header>
      <main className="flex-1">
        <section data-testid="landing-hero" className="mx-auto w-full max-w-7xl px-6 py-16 sm:px-10 lg:py-24 mw-rise">
          <p className="mw-eyebrow">Professional digital identity</p>
          <h1 className="mt-6 max-w-3xl font-serif text-5xl font-normal leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            Your professional identity, <em className="text-gold">remembered.</em>
          </h1>
          <p className="mt-8 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
            One professional identity that can be shared, viewed, saved, and remembered.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <PrimaryCta session={session} testId="create-metworth-button" />
            {!session && (
              <Link to={ROUTES.login} data-testid="signin-button" className="mw-btn mw-btn-ghost">
                Sign in
              </Link>
            )}
          </div>
        </section>
        <DemoShowcase />
        <WhatIsSection />
        <section data-testid="landing-closing-cta" className="border-t border-border">
          <div className="mx-auto w-full max-w-7xl px-6 py-20 sm:px-10 lg:py-28">
            <h2 className="max-w-2xl font-serif text-3xl tracking-tight sm:text-5xl">Build your complete digital presence.</h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Create your MetWorth profile and bring your professional identity together in one place.
            </p>
            <div className="mt-10"><PrimaryCta session={session} testId="closing-cta-button" /></div>
          </div>
        </section>
      </main>
      <footer className="border-t border-border">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-10">
          <ol className="flex flex-wrap gap-x-8 gap-y-2">
            {VERBS.map((v, i) => (
              <li key={v} className="mw-eyebrow">
                <span className="text-gold">0{i + 1}</span> {v}
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} MetWorth</p>
        </div>
      </footer>
    </div>
  );
}
